import React from 'react'
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  string,
  shape
} from 'prop-types'

import { Link } from 'gatsby'
import citygateTheme from '../../theme/'
import { Box, Flex } from 'rebass'
import { Text } from 'rebass'
import NumberFormat from 'react-number-format'

// Shared Components
import { RecentBadge } from '../../utils/style'

// Scoped Components
import {
  InlineInfoWrapper,
  InlineInfo,
  InlineInfoDetails
} from './component/InlineInfo'
import { Favourite } from './component/Favourite'
// import { RegistrationPlate } from './component/RegistrationPlate'
import { VehicleDetails } from './component/VehicleDetails'
import { CTAGroup } from './component/CTAGroup'
import { TextLink } from './component/TextLink'
import { Price, PriceSummary, PriceInstallments } from './component/Price'
import {
  PanelContentWrapper,
  PanelDescription,
  PanelQuestions,
  PanelSmallText,
  PanelTag,
  PanelTagReg,
  // PanelTagFeature,
  PanelTagList,
  PanelTitle,
  panelIconMap,
  PanelOfferTitle,
  PanelOfferDescription
} from './component/PanelElements'

// Icons
import {
  StyledIconHeart,
  StyledIconPhone,
  StyledIconMapPin,
  StyledIconTick
} from './component/StyledIcons'
import { ArrowRight } from './component/ArrowRight'
import IconHeart from '../../vectors/IconHeart'
import IconPhone from '../../vectors/IconPhone'
import IconTickThin from '../../vectors/IconTickThin'
import IconMapPinFlat from '../../vectors/IconMapPinFlat'
import CalltracksPhoneNumber from '../CalltracksPhoneNumber'
import LogoYoutube from '../../vectors/LogoYoutube'

const VideoPlayerPanel = (props) => {
  const {
    favourite,
    onToggleFavourite,
    currency,
    details,
    engine,
    location,
    price,
    model,
    onOffer,
    phoneNumber,
    rawBrand,
    recent,
    registration,
    isReserved,
    hasLibraryImages,
    isSold,
    vatIncluded,
    onReserveEnquiryClick,
    onPaymentClick,
    onBookTestDriveClick,
    onAskQuestionClick,
    canPurchase,
    id,
    reservable,
    category,
    video,
    offer
  } = props

  const highlightColour = onOffer
    ? citygateTheme.colors.orange
    : citygateTheme.colors.white

  const hasOfferText = offer && (offer.offerTitle || offer.offerDescription)

  return (
    <PanelContentWrapper>
      <Favourite mt={['7px', null, 8]}>
        <a
          href="javascript:void(0)"
          onClick={onToggleFavourite}
          aria-label="Favourite"
          className={favourite ? 'favourite' : null}
        >
          <StyledIconHeart>
            <IconHeart />
          </StyledIconHeart>
        </a>
      </Favourite>
      {recent && (
        <RecentBadge mb={10} mr={0}>
          Just Added
        </RecentBadge>
      )}
      <PanelTitle>{model}</PanelTitle>
      <PanelDescription>{engine}</PanelDescription>
      {hasOfferText && (
        <Flex flexDirection="column" mb={12}>
          {offer && offer.offerTitle && (
            <PanelOfferTitle>{offer.offerTitle}</PanelOfferTitle>
          )}
          {offer && offer.offerDescription && (
            <PanelOfferDescription>
              {offer.offerDescription}
            </PanelOfferDescription>
          )}
        </Flex>
      )}
      <PanelTagList>
        {details.map((detail, i) => (
          <PanelTag key={`detail:${i}`}>
            {panelIconMap[detail.label] || null}
            {detail.value}
          </PanelTag>
        ))}
        <PanelTagReg>{registration}</PanelTagReg>
        {/* <PanelTagFeature>Brand New</PanelTagFeature> */}
      </PanelTagList>

      <VehicleDetails
        id="vehicle-details"
        as="header"
        flexWrap="wrap"
        width={12 / 12}
        mt={0}
        alignItems={['initial', 'space-between']}
        highlight={highlightColour}
      >
        <div key="PriceSummarySmallToMediumScreenPlaceholder">
          {isNaN(price.total) && (
            <PriceSummary
              width={[12 / 12]}
              property="offers"
              typeof="Offer"
              px={'5%'}
              py={23}
            >
              <Box>
                <Price highlight={highlightColour}>
                  <span property="price" content={price.total}>
                    <Text fontSize={[6, 7]}>{price.total}</Text>
                    <span />
                  </span>
                </Price>
              </Box>
            </PriceSummary>
          )}
          {!isNaN(price.total) && (
            <PriceSummary property="offers" typeof="Offer">
              <Box>
                <Price highlight={highlightColour}>
                  <Text fontSize={[6, 7]}>
                    <span property="priceCurrency" content={currency.code} />
                    <span property="price" content={price.total}>
                      {onOffer && !isSold && <span>Now&nbsp;</span>}
                      {price.total && !isSold && (
                        <NumberFormat
                          value={price.total}
                          displayType={'text'}
                          thousandSeparator
                          prefix={currency.symbol}
                        />
                      )}
                      {isSold && <b>SOLD</b>}
                      &nbsp;
                    </span>
                  </Text>
                  {isSold || category === 'car' ? null : vatIncluded ? (
                    <Text fontSize={[0, 3]}>inc. VAT</Text>
                  ) : (
                    <Text fontSize={[0, 3]}>plus VAT</Text>
                  )}
                </Price>
                {!isSold &&
                  Object.keys(price.installment).map((type) => {
                    const installment = price.installment[type]
                    return (
                      <PriceInstallments
                        id="price-installments"
                        itemProp="priceSpecification"
                        itemScope
                        itemType="http://schema.org/UnitPriceSpecification"
                        key={type}
                      >
                        <TextLink
                          to="finance-calculator"
                          onClick={() => onPaymentClick(type)}
                          smooth
                          duration={1000}
                        >
                          <strong
                            property="priceCurrency"
                            content={currency.code}
                          />
                          <strong
                            property="price"
                            content={installment.payment}
                          >
                            {installment.payment && (
                              <NumberFormat
                                value={installment.payment}
                                displayType={'text'}
                                thousandSeparator
                                prefix={currency.symbol}
                                decimalScale={2}
                                fixedDecimalScale
                              />
                            )}
                          </strong>
                          /
                          <span itemProp="unitCode" content={installment.code}>
                            {installment.plan} ({type.toUpperCase()}{' '}
                            Representative)
                          </span>
                        </TextLink>
                      </PriceInstallments>
                    )
                  })}
              </Box>
            </PriceSummary>
          )}
        </div>

        <CTAGroup disabled={isReserved}>
          {reservable && !isReserved && price.reserve && (
            <Box onClick={onReserveEnquiryClick} flex="1 1 auto">
              <a href="javascript:void(0);">
                Reserve now -&nbsp;
                <NumberFormat
                  value={price.reserve}
                  displayType={'text'}
                  thousandSeparator
                  prefix={currency.symbol}
                  decimalScale={0}
                  fixedDecimalScale
                />
              </a>
            </Box>
          )}

          {canPurchase && (
            <Box flex="1 1 auto">
              <a
                href={`${process.env.GATSBY_PORTAL_URL}/deal/account/details?vehicle=${id}`}
              >
                Buy Now -&nbsp;
                <NumberFormat
                  value={price.total}
                  displayType={'text'}
                  thousandSeparator
                  prefix={currency.symbol}
                  decimalScale={0}
                  fixedDecimalScale
                />
              </a>
            </Box>
          )}

          <Box onClick={onBookTestDriveClick} flex="1 1 auto">
            <a href="javascript:void(0);">Register Interest</a>
          </Box>

          <Box flex="1 1 auto">
            <a href="#finance-calculator-top">Calculate Finance</a>
          </Box>

          {isReserved && (
            <Box flex="1 1 auto">
              <a disabled>On Reserve</a>
            </Box>
          )}

          {video && (
            <Box className="youtube" flex="1 1 auto">
              <a href={video} target="_blank" rel="noopener noreferrer">
                View this car on <LogoYoutube />
              </a>
            </Box>
          )}
        </CTAGroup>

        {location && (
          <InlineInfo>
            <StyledIconMapPin>
              <IconMapPinFlat />
            </StyledIconMapPin>
            <InlineInfoDetails>
              Location:&nbsp;
              <Link to={`/locations/${location.slug}`}>
                {location.shortName}
              </Link>
            </InlineInfoDetails>
          </InlineInfo>
        )}
        <InlineInfoWrapper>
          <InlineInfo>
            <StyledIconPhone>
              <IconPhone />
            </StyledIconPhone>
            <InlineInfoDetails>
              Call now:&nbsp;
              <CalltracksPhoneNumber
                calltracksClass={phoneNumber.calltracksClass}
                phoneNumber={phoneNumber.phoneNumber}
              />
            </InlineInfoDetails>
          </InlineInfo>
          <InlineInfo>
            <StyledIconTick>
              <IconTickThin />
            </StyledIconTick>
            <InlineInfoDetails>Approved Used {rawBrand}</InlineInfoDetails>
          </InlineInfo>
          <InlineInfo>
            <StyledIconTick>
              <IconTickThin />
            </StyledIconTick>
            <InlineInfoDetails>
              Citygate Satisfaction Guarantee
            </InlineInfoDetails>
          </InlineInfo>
        </InlineInfoWrapper>

        {hasLibraryImages && (
          <PanelSmallText>
            Library images. Actual vehicle may differ from images shown.
          </PanelSmallText>
        )}

        {/* <RegistrationPlate>
        <span>{registration}</span>
      </RegistrationPlate> */}

        <PanelQuestions>
          <Box onClick={onAskQuestionClick} flex="1 1 auto">
            <a href="javascript:void(0);">
              <ArrowRight />
              <span>Ask us anything else</span>
            </a>
          </Box>
        </PanelQuestions>
      </VehicleDetails>
    </PanelContentWrapper>
  )
}

VideoPlayerPanel.defaultProps = {
  location: {},
  phoneNumber: {},
  currency: { code: 'GBP', symbol: '£' }
}

VideoPlayerPanel.propTypes = {
  onReserveEnquiryClick: func,
  onPaymentClick: func,
  onBookTestDriveClick: func,
  onAskQuestionClick: func,
  favourite: bool,
  onToggleFavourite: func,
  currency: shape({
    code: string,
    symbol: string
  }),
  details: arrayOf(
    shape({
      label: string,
      value: oneOfType([number, string])
    })
  ),
  engine: string,
  onOffer: bool,
  registration: string,
  location: shape({
    shortName: string,
    slug: string
  }),
  model: string,
  phoneNumber: shape({
    link: string,
    phoneNumber: string,
    className: string
  }),
  rawBrand: string,
  recent: bool,
  hasLibraryImages: bool,
  isReserved: bool,
  isSold: bool,
  price: shape({
    total: oneOfType([number, string]),
    reserve: number,
    installment: shape({ payment: number, plan: string, code: string })
  }),
  vatIncluded: bool,
  canPurchase: bool,
  id: string,
  category: string,
  video: string
}

export default VideoPlayerPanel
